import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
/**
 * useDomains Hook
 *
 * Hook customizado para gerenciar domínios permitidos com:
 * - Fetch de lista de domínios
 * - Mutations para create, update, delete
 * - Cache automático via React Query
 * - Loading states
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { domainService } from '@/services/domainService';
import { Domain } from '@/types/domain';
import { toast } from 'sonner';

interface UseDomainOptions {
  userId: string | undefined;  // ID do usuário autenticado
  enabled?: boolean;             // Permite controlar quando a query é executada
}

export function useDomains(options: UseDomainOptions) {
  useLanguage();
  const queryClient = useQueryClient();
  const { userId, enabled = true } = options;

  // Query key para cache
  const queryKey = ['domains', userId];

  // Buscar domínios do usuário
  const {
    data: domains = [],
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => domainService.getDomains(userId!),
    enabled: enabled && !!userId, // Só executa se userId existir
    staleTime: 1000 * 60 * 5, // 5 minutos
    gcTime: 1000 * 60 * 10,   // 10 minutos
  });

  // Mutation para criar domínio
  const createMutation = useMutation({
    mutationFn: (domain: string) =>
      domainService.createDomain(userId!, { domain }),
    onSuccess: async () => {
      // Invalidar cache e forçar refetch imediato
      await queryClient.invalidateQueries({
        queryKey,
        refetchType: 'active'
      });
      toast.success(translateInterfaceText("Domínio adicionado com sucesso"));
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || translateInterfaceText("Erro ao adicionar domínio");
      toast.error(errorMessage);
    },
  });

  // Mutation para atualizar domínio
  const updateMutation = useMutation({
    mutationFn: ({ domainId, newDomain }: { domainId: string; newDomain: string }) =>
      domainService.updateDomain(domainId, { domain: newDomain }),
    onSuccess: async () => {
      // Invalidar cache e forçar refetch imediato
      await queryClient.invalidateQueries({
        queryKey,
        refetchType: 'active'
      });
      toast.success(translateInterfaceText("Domínio atualizado com sucesso"));
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || translateInterfaceText("Erro ao atualizar domínio");
      toast.error(errorMessage);
    },
  });

  // Mutation para deletar domínio
  const deleteMutation = useMutation({
    mutationFn: (domainId: string) =>
      domainService.deleteDomain(domainId),
    onSuccess: async () => {
      // Invalidar cache e forçar refetch imediato
      await queryClient.invalidateQueries({
        queryKey,
        refetchType: 'active'
      });
      toast.success(translateInterfaceText("Domínio removido com sucesso"));
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || translateInterfaceText("Erro ao remover domínio");
      toast.error(errorMessage);
    },
  });

  // Helper functions
  const createDomain = async (domain: string) => {
    return createMutation.mutateAsync(domain);
  };

  const updateDomain = async (domainId: string, newDomain: string) => {
    return updateMutation.mutateAsync({ domainId, newDomain });
  };

  const deleteDomain = async (domainId: string) => {
    return deleteMutation.mutateAsync(domainId);
  };

  return {
    // Dados
    domains,

    // Estados de loading
    isLoading,
    isFetching,
    isError,
    error,

    // Estados de mutations
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,

    // Métodos
    createDomain,
    updateDomain,
    deleteDomain,
    refetch,
  };
}

export default useDomains;
